import React, { useEffect, useState } from 'react'

import {
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Pagination } from '@material-ui/lab'
import ApartmentIcon from '@material-ui/icons/Apartment'
import StorefrontIcon from '@material-ui/icons/Storefront'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import VpnKeyIcon from '@material-ui/icons/VpnKey'
import GetAppIcon from '@material-ui/icons/GetApp'
import { toast } from 'react-toastify'

import { SearchField } from '../../../components/SearchField'
import { Selector } from '../../../components/Selector'
import {
    axiosInstance,
    formatPhoneForDisplay,
    toMMDDYYYY,
} from '../../../helpers'
import { useUser } from '../../../hooks'
import { User } from '../../../models'
import { typographyStyle } from '../../../theme/typographyStyle'
import { useOrganizations, useProperties } from '../hooks'

type Order = 'asc' | 'desc'

interface HeadCell {
    id: keyof User | 'actions'
    label: string
    sortable: boolean
}

const headCells: HeadCell[] = [
    { id: 'id', label: 'ID', sortable: true },
    { id: 'name', label: 'Name', sortable: true },
    { id: 'email', label: 'Email', sortable: true },
    { id: 'phone', label: 'Phone', sortable: true },
    { id: 'cell_phone', label: 'Cell Phone', sortable: true },
    { id: 'created_date', label: 'Created Date', sortable: true },
    { id: 'actions', label: '', sortable: false },
]

const CSV_HEADERS = [
    'ID',
    'Name',
    'Email',
    'Phone',
    'Cell Phone',
    'Created Date',
    'Regional Manager',
    'Property Manager',
]

const formatCsvValue = (
    value: string | number | boolean | null | undefined,
) => {
    const stringValue = value === null || value === undefined ? '' : `${value}`
    return `"${stringValue.replace(/"/g, '""')}"`
}

const buildUserCsv = (users: User[]) => {
    const rows = users.map((user) => [
        user.id,
        user.name,
        user.email,
        user.phone,
        user.cell_phone,
        user.created_date ? toMMDDYYYY(new Date(user.created_date)) : '',
        user.regional_manager ? 'Yes' : 'No',
        user.property_manager ? 'Yes' : 'No',
    ])

    return [CSV_HEADERS, ...rows]
        .map((row) => row.map(formatCsvValue).join(','))
        .join('\n')
}

const useStyles = makeStyles((theme) => ({
    tableCell: { flex: 1 },
    headerCell: { flex: 1 },
    tableHead: { backgroundColor: theme.palette.background.paper },
    stickyHeader: { position: 'sticky', top: 0, zIndex: 1 },
    tableContainer: {
        maxHeight: 'calc(100vh - 200px)',
        minHeight: 'calc(100vh - 200px)',
        overflowY: 'auto',
        width: '100%',
    },
    paginationContainer: {
        borderTop: `1px solid ${theme.palette.grey[400]}`,
        padding: theme.spacing(1),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        width: '100%',
        maxWidth: 'none',
        backgroundColor: theme.palette.background.paper,
    },
    rootContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: 0,
        maxWidth: 'none',
    },
    roleChips: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.75) },
    regionalManagerChip: { fontWeight: 700 },
    propertyManagerChip: {
        border: '1px solid #1e88e5',
        color: '#1e88e5',
        fontWeight: 700,
    },
}))

// ─── Workspace modal ──────────────────────────────────────────────────────────

interface WorkspaceInfo {
    id: number
    name: string
    active_workspace: {
        name: string
        address: string
        city: string
        state: string
        zip_code: string
        company_type: string
    }
}

interface WorkspaceModalProps {
    user: User | null
    onClose: () => void
}

const WorkspaceModal = ({ user, onClose }: WorkspaceModalProps) => {
    const theme = useTheme()
    const [workspaces, setWorkspaces] = useState<WorkspaceInfo[]>([])
    const [loading, setLoading] = useState(false)

    const primary = theme.palette.primary.main
    const grey100 = theme.palette.grey[100]
    const grey300 = theme.palette.grey[300]

    useEffect(() => {
        if (!user) return
        setLoading(true)
        setWorkspaces([])
        axiosInstance
            .post('user/find_user_by_email/', { email: user.email })
            .then((res) => setWorkspaces(res.data.user?.workspaces ?? []))
            .catch(() => toast.error('Could not load workspaces'))
            .finally(() => setLoading(false))
    }, [user])

    return (
        <Dialog open={!!user} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <div>
                    <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 700, lineHeight: 1.2 }}
                    >
                        {user?.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {user?.email}
                    </Typography>
                </div>
            </DialogTitle>

            <DialogContent
                dividers
                style={{ padding: 0, height: '60vh', overflowY: 'auto' }}
            >
                {loading ? (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: theme.spacing(4),
                        }}
                    >
                        <CircularProgress size={32} />
                    </div>
                ) : workspaces.length === 0 ? (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{
                            padding: theme.spacing(3),
                            textAlign: 'center',
                        }}
                    >
                        No workspaces found
                    </Typography>
                ) : (
                    <div style={{ padding: theme.spacing(1.5) }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            <Typography variant="body2" color="textSecondary">
                                Workspaces
                            </Typography>
                            <Chip
                                label={workspaces.length}
                                size="small"
                                style={{
                                    marginLeft: theme.spacing(1),
                                    backgroundColor: primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    height: 18,
                                }}
                            />
                        </div>

                        {workspaces.map((ws) => {
                            const isApartment =
                                ws.active_workspace.company_type === 'APARTMENT'
                            const accentColor = isApartment
                                ? primary
                                : theme.palette.secondary.main
                            const addressParts = [
                                ws.active_workspace.address,
                                ws.active_workspace.city,
                                ws.active_workspace.state,
                                ws.active_workspace.zip_code,
                            ]
                                .filter(Boolean)
                                .join(', ')

                            return (
                                <div
                                    key={ws.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: theme.spacing(1.25, 1.5),
                                        marginBottom: theme.spacing(1),
                                        borderRadius: 8,
                                        border: `1px solid ${grey300}`,
                                        borderLeft: `4px solid ${accentColor}`,
                                        backgroundColor: grey100,
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 30,
                                            height: 30,
                                            borderRadius: 7,
                                            backgroundColor: accentColor + '1a',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: theme.spacing(1.5),
                                            flexShrink: 0,
                                            color: accentColor,
                                        }}
                                    >
                                        {isApartment ? (
                                            <ApartmentIcon
                                                style={{ fontSize: 16 }}
                                            />
                                        ) : (
                                            <StorefrontIcon
                                                style={{ fontSize: 16 }}
                                            />
                                        )}
                                    </div>

                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            style={{ fontWeight: 600 }}
                                            noWrap
                                        >
                                            {ws.active_workspace.name}
                                        </Typography>
                                        {addressParts && (
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                noWrap
                                            >
                                                {addressParts}
                                            </Typography>
                                        )}
                                    </div>

                                    <Chip
                                        label={
                                            isApartment ? 'Apartment' : 'Vendor'
                                        }
                                        size="small"
                                        style={{
                                            marginLeft: theme.spacing(1),
                                            backgroundColor: accentColor + '1a',
                                            color: accentColor,
                                            fontWeight: 700,
                                            fontSize: 10,
                                            height: 18,
                                            flexShrink: 0,
                                        }}
                                    />
                                </div>
                            )
                        })}
                    </div>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="outlined" size="small">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}

// ─── Change password modal ────────────────────────────────────────────────────

interface ChangePasswordModalProps {
    user: User | null
    onClose: () => void
}

const ChangePasswordModal = ({ user, onClose }: ChangePasswordModalProps) => {
    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')

    const mismatch = confirm.length > 0 && password !== confirm

    const handleClose = () => {
        setPassword('')
        setConfirm('')
        onClose()
    }

    const handleSubmit = () => {
        if (!user || !password || mismatch) return
        axiosInstance
            .post('user/admin-set-password/', {
                email: user.email,
                password,
            })
            .then(() => {
                toast.success(`Password updated for ${user.email}`)
                handleClose()
            })
            .catch((e) => toast.error(`Failed: ${e}`))
    }

    return (
        <Dialog open={!!user} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    Change Password
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {user?.email}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    label="New password"
                    type="password"
                    variant="outlined"
                    size="small"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ marginBottom: 12 }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSubmit()
                    }}
                />
                <TextField
                    fullWidth
                    label="Confirm password"
                    type="password"
                    variant="outlined"
                    size="small"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    error={mismatch}
                    helperText={mismatch ? 'Passwords do not match' : ''}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSubmit()
                    }}
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} variant="outlined" size="small">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={!password || mismatch}
                >
                    Update Password
                </Button>
            </DialogActions>
        </Dialog>
    )
}

// ─── User list ────────────────────────────────────────────────────────────────

export const UserList = () => {
    const { fullUserList, actions } = useUser()
    const {
        organizationList,
        isLoading: organizationsLoading,
    } = useOrganizations()
    const { propertyList, isLoading: propertiesLoading } = useProperties()
    const [searchQuery, setSearchQuery] = useState('')
    const [showRegionalManagersOnly, setShowRegionalManagersOnly] = useState(
        false,
    )
    const [showPropertyManagersOnly, setShowPropertyManagersOnly] = useState(
        false,
    )
    const [createdDateFrom, setCreatedDateFrom] = useState('')
    const [createdDateTo, setCreatedDateTo] = useState('')
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<
        number | null
    >(null)
    const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(
        null,
    )
    const [loading, setLoading] = useState(true)
    const [order, setOrder] = useState<Order>('asc')
    const [orderBy, setOrderBy] = useState<keyof User>('name')
    const [page, setPage] = useState(1)
    const itemsPerPage = 25

    const [workspaceModalUser, setWorkspaceModalUser] = useState<User | null>(
        null,
    )
    const [passwordModalUser, setPasswordModalUser] = useState<User | null>(
        null,
    )

    const theme = useTheme()
    const classes = useStyles()

    const organizations = organizationList?.data ?? []
    const properties = propertyList?.data ?? []

    const sortedOrganizations = React.useMemo(() => {
        return [...organizations].sort((a, b) => a.name.localeCompare(b.name))
    }, [organizations])

    const filteredProperties = React.useMemo(() => {
        const organizationProperties = selectedOrganizationId
            ? properties.filter(
                  (property) =>
                      property.organization.id === selectedOrganizationId,
              )
            : properties

        return [...organizationProperties].sort((a, b) =>
            a.name.localeCompare(b.name),
        )
    }, [properties, selectedOrganizationId])

    useEffect(() => {
        const params = selectedPropertyId
            ? { company_id: selectedPropertyId }
            : selectedOrganizationId
            ? { organization_id: selectedOrganizationId }
            : undefined

        setLoading(true)
        actions.getFullUserList(params).finally(() => setLoading(false))
    }, [selectedOrganizationId, selectedPropertyId])

    useEffect(() => {
        setPage(1)
    }, [
        searchQuery,
        showRegionalManagersOnly,
        showPropertyManagersOnly,
        createdDateFrom,
        createdDateTo,
        selectedOrganizationId,
        selectedPropertyId,
    ])

    const handleOrganizationChange = (
        event: React.ChangeEvent<{ value: unknown }>,
    ) => {
        const nextOrganizationId = Number(event.target.value)
        setSelectedOrganizationId(
            nextOrganizationId === -1 ? null : nextOrganizationId,
        )
        setSelectedPropertyId(null)
    }

    const handlePropertyChange = (
        event: React.ChangeEvent<{ value: unknown }>,
    ) => {
        const nextPropertyId = Number(event.target.value)
        setSelectedPropertyId(nextPropertyId === -1 ? null : nextPropertyId)
    }

    const handleRequestSort = (property: keyof User) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const filteredUsers = React.useMemo(() => {
        return fullUserList.filter((user) => {
            const searchLower = searchQuery.toLowerCase()
            const matchesSearch =
                user.name?.toLowerCase().includes(searchLower) ||
                user.email?.toLowerCase().includes(searchLower) ||
                user.phone?.toLowerCase().includes(searchLower) ||
                user.cell_phone?.toLowerCase().includes(searchLower)

            const isRegionalManager = Boolean(user.regional_manager)
            const isPropertyManager = Boolean(user.property_manager)
            let matchesRoleFilter = true
            if (showRegionalManagersOnly && showPropertyManagersOnly) {
                matchesRoleFilter = isRegionalManager || isPropertyManager
            } else if (showRegionalManagersOnly) {
                matchesRoleFilter = isRegionalManager
            } else if (showPropertyManagersOnly) {
                matchesRoleFilter = isPropertyManager
            }

            let matchesDateFilter = true
            if (createdDateFrom || createdDateTo) {
                const userDate = user.created_date
                    ? new Date(user.created_date as string | Date).getTime()
                    : 0
                if (createdDateFrom) {
                    const fromStart = new Date(createdDateFrom).setHours(
                        0,
                        0,
                        0,
                        0,
                    )
                    if (userDate < fromStart) matchesDateFilter = false
                }
                if (createdDateTo && matchesDateFilter) {
                    const toEnd = new Date(createdDateTo).setHours(
                        23,
                        59,
                        59,
                        999,
                    )
                    if (userDate > toEnd) matchesDateFilter = false
                }
            }

            return matchesSearch && matchesRoleFilter && matchesDateFilter
        })
    }, [
        fullUserList,
        searchQuery,
        showRegionalManagersOnly,
        showPropertyManagersOnly,
        createdDateFrom,
        createdDateTo,
    ])

    const sortedUsers = React.useMemo(() => {
        const comparator = (a: User, b: User) => {
            const aValue = a[orderBy]
            const bValue = b[orderBy]
            if (!aValue && !bValue) return 0
            if (!aValue) return 1
            if (!bValue) return -1
            if (orderBy === 'created_date') {
                return order === 'desc'
                    ? new Date(bValue as string | Date).getTime() -
                          new Date(aValue as string | Date).getTime()
                    : new Date(aValue as string | Date).getTime() -
                          new Date(bValue as string | Date).getTime()
            }
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return order === 'desc' ? bValue - aValue : aValue - bValue
            }
            const aStr = String(aValue).toLowerCase()
            const bStr = String(bValue).toLowerCase()
            return order === 'desc'
                ? bStr.localeCompare(aStr)
                : aStr.localeCompare(bStr)
        }
        return [...filteredUsers].sort(comparator)
    }, [filteredUsers, order, orderBy])

    const handleDownloadCsv = () => {
        if (!sortedUsers.length) {
            toast.info('No users to download.')
            return
        }

        const csv = buildUserCsv(sortedUsers)
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        const date = new Date().toISOString().split('T')[0]

        link.href = url
        link.download = `users-${date}.csv`
        link.click()
        URL.revokeObjectURL(url)
    }

    const start = (page - 1) * itemsPerPage
    const end = Math.min(start + itemsPerPage, sortedUsers.length)
    const numPages = Math.ceil(sortedUsers.length / itemsPerPage)
    const pageData = sortedUsers.slice(start, end)

    return (
        <Container
            disableGutters
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                padding: theme.spacing(2),
                maxWidth: 'none',
                margin: 0,
            }}
        >
            {/* Filters */}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    marginBottom: theme.spacing(2),
                    gap: theme.spacing(1.5),
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            fontWeight: 'bold',
                        }}
                    >
                        User List
                    </span>
                    <Button
                        color="primary"
                        disabled={loading || sortedUsers.length === 0}
                        onClick={handleDownloadCsv}
                        size="small"
                        startIcon={<GetAppIcon />}
                        variant="contained"
                    >
                        Download CSV
                    </Button>
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: theme.spacing(1.5),
                        width: '100%',
                    }}
                >
                    <SearchField
                        value={searchQuery}
                        style={{ maxWidth: 300 }}
                        variant="outlined"
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        placeholder="Search"
                    />
                    <Chip
                        label="Regional Managers"
                        clickable
                        color={showRegionalManagersOnly ? 'primary' : 'default'}
                        variant={
                            showRegionalManagersOnly ? 'default' : 'outlined'
                        }
                        onClick={() =>
                            setShowRegionalManagersOnly((prev) => !prev)
                        }
                    />
                    <Chip
                        label="Property Managers"
                        clickable
                        color={showPropertyManagersOnly ? 'primary' : 'default'}
                        variant={
                            showPropertyManagersOnly ? 'default' : 'outlined'
                        }
                        onClick={() =>
                            setShowPropertyManagersOnly((prev) => !prev)
                        }
                    />
                    <Selector
                        label="Organization"
                        currentValue={selectedOrganizationId ?? -1}
                        onChange={handleOrganizationChange}
                        data={sortedOrganizations}
                        getDisplayString={(organization) => organization.name}
                        searchable
                        noSelectionLabel="All Organizations"
                        size="small"
                        variant="outlined"
                        customStyle={{
                            formControl: { minWidth: 220 },
                        }}
                        disabled={organizationsLoading}
                        maxItems={25}
                        popupHeight={420}
                    />
                    <Selector
                        label="Property"
                        currentValue={selectedPropertyId ?? -1}
                        onChange={handlePropertyChange}
                        data={filteredProperties}
                        getDisplayString={(property) =>
                            property.id === -1
                                ? property.name
                                : selectedOrganizationId
                                ? property.name
                                : `${property.name} (${property.organization.name})`
                        }
                        searchable
                        noSelectionLabel="All Properties"
                        size="small"
                        variant="outlined"
                        customStyle={{
                            formControl: { minWidth: 240 },
                        }}
                        disabled={propertiesLoading}
                        maxItems={25}
                        popupHeight={420}
                    />
                    <TextField
                        label="Created from"
                        type="date"
                        value={createdDateFrom}
                        onChange={(e) => setCreatedDateFrom(e.target.value)}
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        style={{ width: 160 }}
                    />
                    <TextField
                        label="Created to"
                        type="date"
                        value={createdDateTo}
                        onChange={(e) => setCreatedDateTo(e.target.value)}
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        style={{ width: 160 }}
                    />
                </div>
            </div>

            {loading ? (
                <Container
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    <CircularProgress size={50} />
                </Container>
            ) : (
                <Container className={classes.rootContainer}>
                    <TableContainer className={classes.tableContainer}>
                        <Table stickyHeader style={{ width: '100%' }}>
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    {headCells.map((headCell) => (
                                        <TableCell
                                            key={headCell.id}
                                            className={`${classes.headerCell} ${classes.stickyHeader}`}
                                        >
                                            {headCell.sortable ? (
                                                <TableSortLabel
                                                    active={
                                                        orderBy === headCell.id
                                                    }
                                                    direction={
                                                        orderBy === headCell.id
                                                            ? order
                                                            : 'asc'
                                                    }
                                                    onClick={() =>
                                                        handleRequestSort(
                                                            headCell.id as keyof User,
                                                        )
                                                    }
                                                >
                                                    {headCell.label}
                                                </TableSortLabel>
                                            ) : (
                                                <span>{headCell.label}</span>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pageData.map((user) => (
                                    <TableRow key={user.id}>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            {user.id}
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: theme.spacing(0.5),
                                                }}
                                            >
                                                <span>{user.name}</span>
                                                {(user.regional_manager ||
                                                    user.property_manager) && (
                                                    <div
                                                        className={
                                                            classes.roleChips
                                                        }
                                                    >
                                                        {user.regional_manager && (
                                                            <Chip
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                                label="Regional Manager"
                                                                className={
                                                                    classes.regionalManagerChip
                                                                }
                                                            />
                                                        )}
                                                        {user.property_manager && (
                                                            <Chip
                                                                size="small"
                                                                variant="outlined"
                                                                label="Property Manager"
                                                                className={
                                                                    classes.propertyManagerChip
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            {user.email}
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            {formatPhoneForDisplay(user.phone)}
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            {formatPhoneForDisplay(
                                                user.cell_phone,
                                            )}
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                        >
                                            {user.created_date
                                                ? toMMDDYYYY(
                                                      new Date(
                                                          user.created_date,
                                                      ),
                                                  )
                                                : ''}
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCell}
                                            align="right"
                                        >
                                            <Tooltip title="View workspaces">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setWorkspaceModalUser(
                                                            user,
                                                        )
                                                    }
                                                >
                                                    <InfoOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Change password">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setPasswordModalUser(
                                                            user,
                                                        )
                                                    }
                                                    style={{ marginLeft: 4 }}
                                                >
                                                    <VpnKeyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Container className={classes.paginationContainer}>
                        <div style={{ flex: 1 }} />
                        <Pagination
                            count={numPages}
                            page={page}
                            onChange={(_: unknown, newPage: number) =>
                                setPage(newPage)
                            }
                        />
                        <div style={{ flex: 1, textAlign: 'right' }}>
                            {start + 1} – {end} of {sortedUsers.length}
                        </div>
                    </Container>
                </Container>
            )}

            <WorkspaceModal
                user={workspaceModalUser}
                onClose={() => setWorkspaceModalUser(null)}
            />
            <ChangePasswordModal
                user={passwordModalUser}
                onClose={() => setPasswordModalUser(null)}
            />
        </Container>
    )
}
